import { Action } from "redux";
import { EnhancedAxiosError, TaskStatusModel, TaskGroupModel, OperationalTargetModel } from "src/api";

export enum QualityHubActionTypes {
    /**
     * An action that indicates a task list has been created.
     */
    TaskListCreated = "TaskListCreated",

    /**
     * An action that indicates a task list has been updated.
     */
    TaskListUpdated = "TaskListUpdated",

    /**
     * An action that indicates a task list has been deleted.
     */
    TaskListDeleted = "TaskListDeleted",

    /**
     * An action that indicates a task list group has been created.
     */
    TaskListGroupCreated = "TaskListGroupCreated",

    /**
     * An action that indicates a task list group has been updated.
     */
    TaskListGroupUpdated = "TaskListGroupUpdated",

    /**
     * An action that indicates a task list group has been deleted.
     */
    TaskListGroupDeleted = "TaskListGroupDeleted",

    /**
     * An action that indicates a task list item has been created.
     */
    TaskListItemCreated = "TaskListItemCreated",

    /**
     * An action that indicates a task list item has been updated.
     */
    TaskListItemUpdated = "TaskListItemUpdated",

    /**
     * An action that indicates task list group overrides are being updated.
     */
    SetTaskListGroupOverrides = "SetTaskListGroupOverrides",

    /**
     * A thunk action that indicates task list group overrides update is pending.
     */
    SetTaskListGroupOverridesPending = "SetTaskListGroupOverrides_PENDING",

    /**
     * A thunk action that indicates task list group overrides update has failed.
     */
    SetTaskListGroupOverridesRejected = "SetTaskListGroupOverrides_REJECTED",

    /**
     * A thunk action that indicates task list group overrides update has succeeded.
     */
    SetTaskListGroupOverridesFulfilled = "SetTaskListGroupOverrides_FULFILLED",

    /**
     * An action that indicates task list item overrides are being updated.
     */
    UpdateTaskListItemOverrides = "UpdateTaskListItemOverrides",

    /**
     * A thunk action that indicates task list item overrides update is pending.
     */
    UpdateTaskListItemOverridesPending = "UpdateTaskListItemOverrides_PENDING",

    /**
     * A thunk action that indicates task list item overrides update has failed.
     */
    UpdateTaskListItemOverridesRejected = "UpdateTaskListItemOverrides_REJECTED",

    /**
     * A thunk action that indicates task list item overrides update has succeeded.
     */
    UpdateTaskListItemOverridesFulfilled = "UpdateTaskListItemOverrides_FULFILLED",

    /**
     * An action that indicates a task list item has been deleted.
     */
    TaskListItemDeleted = "TaskListItemDeleted",

    /**
     * An action that indicates a task list item status has been created.
     */
    TaskListItemStatusCreated = "TaskListItemStatusCreated",

    /**
     * An action that indicates a task list item status has been updated.
     */
    TaskListItemStatusUpdated = "TaskListItemStatusUpdated",

    /**
     * An action that indicates a task list item status has been deleted.
     */
    TaskListItemStatusDeleted = "TaskListItemStatusDeleted",

    /**
     * An action that indicates a resource tag has been created.
     */
    ResourceTagCreated = "ResourceTagCreated",

    /**
     * An action that indicates a resource tag has been updated.
     */
    ResourceTagUpdated = "ResourceTagUpdated",

    /**
     * An action that indicates a resource tag has been deleted.
     */
    ResourceTagDeleted = "ResourceTagDeleted",

    /**
     * The base action for querying my task lists.
     */
    QueryMyTaskLists = "QueryMyTaskLists",

    /**
     * A thunk action that indicates my task lists are being queried.
     */
    QueryMyTaskListsPending = "QueryMyTaskLists_PENDING",

    /**
     * A thunk action that indicates my task lists query has failed.
     */
    QueryMyTaskListsRejected = "QueryMyTaskLists_REJECTED",

    /**
     * A thunk action that indicates my task lists query has succeeded.
     */
    QueryMyTaskListsFulfilled = "QueryMyTaskLists_FULFILLED",

    /**
     * The base action for finding a single task list.
     */
    FindTaskList = "FindTaskList",

    /**
     * A thunk action that indicates a task list is being looked up.
     */
    FindTaskListPending = "FindTaskList_PENDING",

    /**
     * A thunk action that indicates a task list lookup has failed.
     */
    FindTaskListRejected = "FindTaskList_REJECTED",

    /**
     * A thunk action that indicates a task list lookup has succeeded.
     */
    FindTaskListFulfilled = "FindTaskList_FULFILLED",

    /**
     * The base action for creating a task list status.
     */
    CreateTaskStatus = "CreateTaskStatus",

    /**
     * A thunk action that indicates a task list status creation is pending.
     */
    CreateTaskStatusPending = "CreateTaskStatus_PENDING",

    /**
     * A thunk action that indicates a task list status creation has failed.
     */
    CreateTaskStatusRejected = "CreateTaskStatus_REJECTED",

    /**
     * A thunk action that indicates a task list status creation has succeeded.
     */
    CreateTaskStatusFulfilled = "CreateTaskStatus_FULFILLED",

    /**
     * The base action for updating a task list status.
     */
    UpdateTaskStatus = "UpdateTaskStatus",

    /**
     * A thunk action that indicates a task list status update is pending.
     */
    UpdateTaskStatusPending = "UpdateTaskStatus_PENDING",

    /**
     * A thunk action that indicates a task list status update has failed.
     */
    UpdateTaskStatusRejected = "UpdateTaskStatus_REJECTED",

    /**
     * A thunk action that indicates a task list status update has succeeded.
     */
    UpdateTaskStatusFulfilled = "UpdateTaskStatus_FULFILLED",

    /**
     * The base action for deleting a task list status.
     */
    DeleteTaskStatus = "DeleteTaskStatus",

    /**
     * A thunk action that indicates a task list status deletion is pending.
     */
    DeleteTaskStatusPending = "DeleteTaskStatus_PENDING",

    /**
     * A thunk action that indicates a task list status deletion has failed.
     */
    DeleteTaskStatusRejected = "DeleteTaskStatus_REJECTED",

    /**
     * A thunk action that indicates a task list status deletion has succeeded.
     */
    DeleteTaskStatusFulfilled = "DeleteTaskStatus_FULFILLED",

    /**
     * The base action for querying resource tags.
     */
    QueryResourceTags = "QueryResourceTags",

    /**
     * A thunk action that indicates resource tags are being queried.
     */
    QueryResourceTagsPending = "QueryResourceTags_PENDING",

    /**
     * A thunk action that indicates resource tags query has failed.
     */
    QueryResourceTagsRejected = "QueryResourceTags_REJECTED",

    /**
     * A thunk action that indicates resource tags query has succeeded.
     */
    QueryResourceTagsFulfilled = "QueryResourceTags_FULFILLED",

    /**
     * An action that indicates an operation should be added to the offline
     * queue.
     */
    AddToOfflineQueue = "QH_AddToOfflineQueue",

    /**
     * An action that indicates a task should be removed from the offline queue.
     */
    RemoveFromOfflineQueue = "QH_RemoveFromOfflineQueue",

    QueryOperationalTargets = "QueryOperationalTargets",
    QueryOperationalTargetsPending = "QueryOperationalTargets_PENDING",
    QueryOperationalTargetsRejected = "QueryOperationalTargets_REJECTED",
    QueryOperationalTargetsFulfilled = "QueryOperationalTargets_FULFILLED",

    CreateOperationalTarget = "CreateOperationalTarget",
    CreateOperationalTargetPending = "CreateOperationalTarget_PENDING",
    CreateOperationalTargetRejected = "CreateOperationalTarget_REJECTED",
    CreateOperationalTargetFulfilled = "CreateOperationalTarget_FULFILLED",

    UpdateOperationalTarget = "UpdateOperationalTarget",
    UpdateOperationalTargetPending = "UpdateOperationalTarget_PENDING",
    UpdateOperationalTargetRejected = "UpdateOperationalTarget_REJECTED",
    UpdateOperationalTargetFulfilled = "UpdateOperationalTarget_FULFILLED",

    DeleteOperationalTarget = "DeleteOperationalTarget",
    DeleteOperationalTargetPending = "DeleteOperationalTarget_PENDING",
    DeleteOperationalTargetRejected = "DeleteOperationalTarget_REJECTED",
    DeleteOperationalTargetFulfilled = "DeleteOperationalTarget_FULFILLED",

    OperationalTargetCreated = "QH_OpsTarget_Created",
    OperationalTargetUpdated = "QH_OpsTarget_Updated",
    OperationalTargetDeleted = "QH_OpsTarget_Deleted",
}

/**
 * An action that indicates a task list has been created.
 */
export type TaskListCreatedAction
    = Action<QualityHubActionTypes.TaskListCreated>
    & { list: Spintr.TaskList; };

/**
 * An action that indicates a task list has been updated.
 */
export type TaskListUpdatedAction
    = Action<QualityHubActionTypes.TaskListUpdated>
    & { list: Spintr.TaskList; };

/**
 * An action that indicates a task list has been deleted.
 */
export type TaskListDeletedAction
    = Action<QualityHubActionTypes.TaskListDeleted>
    & { id: number; };

/**
 * An action that indicates a task list group has been created.
 */
export type TaskListGroupCreatedAction
    = Action<QualityHubActionTypes.TaskListGroupCreated>
    & { group: Spintr.TaskListGroup; };

/**
 * An action that indicates a task list group has been updated.
 */
export type TaskListGroupUpdatedAction
    = Action<QualityHubActionTypes.TaskListGroupUpdated>
    & { group: Spintr.TaskListGroup; };

/**
 * An action that indicates a task list group has been deleted.
 */
export type TaskListGroupDeletedAction
    = Action<QualityHubActionTypes.TaskListGroupDeleted>
    & { groupId: number; listId: number; };

/**
 * An action that indicates a task list item has been created.
 */
export type TaskListItemCreatedAction
    = Action<QualityHubActionTypes.TaskListItemCreated>
    & { item: Spintr.TaskListTask; };

/**
 * An action that indicates a task list item has been updated.
 */
export type TaskListItemUpdatedAction
    = Action<QualityHubActionTypes.TaskListItemUpdated>
    & { item: Spintr.TaskListTask; };

/**
 * The metadata associated with the SetTaskListGroupOverrides actions.
 */
export type SetTaskListGroupOverridesMetadata = {
    /**
     * The ID of the task list.
     */
    listId: number;

    /**
     * The overrides that were sent to the server.
     */
    overrides: Record<string, Partial<TaskGroupModel>>;
};

/**
 * An action that indicates task list group overrides are being updated.
 */
export type SetTaskListGroupOverridesAction
    = Action<QualityHubActionTypes.SetTaskListGroupOverrides>
    & ActionWithMetadata<SetTaskListGroupOverridesMetadata>
    & {
        /**
         * The promise to be handled by the thunk middleware.
         */
        payload: Promise<void>;
    };

/**
 * An action that indicates task list group overrides update is pending.
 */
export type SetTaskListGroupOverridesPendingAction
    = Action<QualityHubActionTypes.SetTaskListGroupOverridesPending>
    & ActionWithMetadata<SetTaskListGroupOverridesMetadata>;

/**
 * An action that indicates task list group overrides update has failed.
 */
export type SetTaskListGroupOverridesRejectedAction
    = Action<QualityHubActionTypes.SetTaskListGroupOverridesRejected>
    & ActionWithMetadata<SetTaskListGroupOverridesMetadata>
    & AxiosErrorAction;

/**
 * An action that indicates task list group overrides update has succeeded.
 */
export type SetTaskListGroupOverridesFulfilledAction
    = Action<QualityHubActionTypes.SetTaskListGroupOverridesFulfilled>
    & ActionWithMetadata<SetTaskListGroupOverridesMetadata>;

/**
 * The metadata associated with the UpdateTaskListItemOverrides actions.
 */
export type UpdateTaskListItemOverridesMetadata = {
    /**
     * The ID of the task list.
     */
    listId: number;

    /**
     * The overrides that were sent to the server.
     */
    overrides: Record<string, Partial<Spintr.TaskListTask>>;
};

/**
 * An action that indicates task list item overrides are being updated.
 */
export type UpdateTaskListItemOverridesAction
    = Action<QualityHubActionTypes.UpdateTaskListItemOverrides>
    & ActionWithMetadata<UpdateTaskListItemOverridesMetadata>
    & {
        /**
         * The promise to be handled by the thunk middleware.
         */
        payload: Promise<void>;
    };

/**
 * An action that indicates task list item overrides update is pending.
 */
export type UpdateTaskListItemOverridesPendingAction
    = Action<QualityHubActionTypes.UpdateTaskListItemOverridesPending>
    & ActionWithMetadata<UpdateTaskListItemOverridesMetadata>;

/**
 * An action that indicates task list item overrides update has failed.
 */
export type UpdateTaskListItemOverridesRejectedAction
    = Action<QualityHubActionTypes.UpdateTaskListItemOverridesRejected>
    & ActionWithMetadata<UpdateTaskListItemOverridesMetadata>
    & AxiosErrorAction;

/**
 * An action that indicates task list item overrides update has succeeded.
 */
export type UpdateTaskListItemOverridesFulfilledAction
    = Action<QualityHubActionTypes.UpdateTaskListItemOverridesFulfilled>
    & ActionWithMetadata<UpdateTaskListItemOverridesMetadata>;

/**
 * An action that indicates a task list item has been deleted.
 */
export type TaskListItemDeletedAction
    = Action<QualityHubActionTypes.TaskListItemDeleted>
    & { itemId: number; listId: number; };

/**
 * An action that indicates a task list item status has been created.
 */
export type TaskListItemStatusCreatedAction
    = Action<QualityHubActionTypes.TaskListItemStatusCreated>
    & { listId: number; status: Spintr.TaskListItemStatus; };

/**
 * An action that indicates a task list item status has been created.
 */
export type TaskListItemStatusUpdatedAction
    = Action<QualityHubActionTypes.TaskListItemStatusUpdated>
    & { listId: number; status: Spintr.TaskListItemStatus; };

/**
 * An action that indicates a task list item status has been created.
 */
export type TaskListItemStatusDeletedAction
    = Action<QualityHubActionTypes.TaskListItemStatusDeleted>
    & { listId: number; statusId: number; };

/**
 * An action that indicates a resource tag has been created.
 */
export type ResourceTagCreatedAction
    = Action<QualityHubActionTypes.ResourceTagCreated> & {
    /**
     * The tag that was created.
     */
    tag: Spintr.ResourceTag;
};

/**
 * An action that indicates a resource tag has been updated.
 */
export type ResourceTagUpdatedAction
    = Action<QualityHubActionTypes.ResourceTagUpdated> & {
    /**
     * The tag that was updated.
     */
    tag: Spintr.ResourceTag;
};

/**
 * An action that indicates a resource tag has been deleted.
 */
export type ResourceTagDeletedAction
    = Action<QualityHubActionTypes.ResourceTagDeleted> & {
    /**
     * The ID of the tag that was deleted.
     */
    tagId: number;
};

/**
 * An action that has metadata associated with it.
 */
type ActionWithMetadata<TType = any | undefined> = {
    /**
     * The metadata associated with the action.
     */
    meta: TType;
};

type AxiosErrorAction = {
    /**
     * Indicates that an error occurred during the query.
     */
    error: true;

    /**
     * The AxiosError that occurred during the query.
     */
    payload: EnhancedAxiosError;
};

/**
 * The metadata associated with the QueryMyTaskListsAction.
 */
type QueryMyTaskListsMetadata = {
    /**
     * The ID of the node for which the task lists are being queried, if any.
     */
    nodeId?: number | undefined;
};

/**
 * An action that indicates my task lists will be queried.
 */
export type QueryMyTaskListsAction
    = Action<QualityHubActionTypes.QueryMyTaskLists>
    & ActionWithMetadata<QueryMyTaskListsMetadata>
    & {
        /**
         * The promise to be handled by the thunk middleware.
         */
        payload: Promise<Spintr.TaskList[]>;
    };

/**
 * An action that indicates that my task lists are being queried.
 */
export type QueryMyTaskListsPendingAction
    = Action<QualityHubActionTypes.QueryMyTaskListsPending>
    & ActionWithMetadata<QueryMyTaskListsMetadata>;

/**
 * An action that indicates that my task lists query has failed.
 */
export type QueryMyTaskListsRejectedAction
    = Action<QualityHubActionTypes.QueryMyTaskListsRejected>
    & ActionWithMetadata<QueryMyTaskListsMetadata>
    & AxiosErrorAction;

/**
 * An action that indicates that my task lists query has succeeded.
 */
export type QueryMyTaskListsFulfilledAction
    = Action<QualityHubActionTypes.QueryMyTaskListsFulfilled>
    & ActionWithMetadata<QueryMyTaskListsMetadata>
    & {
        /**
         * The resolved promise of my task lists.
         */
        payload: Spintr.TaskList[];
    };

/**
 * The metadata associated with the FindTaskListAction.
 */
type FindTaskListMetadata = {
    /**
     * The ID of the task list being looked up.
     */
    id: number;
};

export type FindTaskListAction
    = Action<QualityHubActionTypes.FindTaskList>
    & ActionWithMetadata<FindTaskListMetadata>
    & {
        /**
         * The promise to be handled by the thunk middleware.
         */
        payload: Promise<Spintr.TaskList>;
    };

export type FindTaskListPendingAction
    = Action<QualityHubActionTypes.FindTaskListPending>
    & ActionWithMetadata<FindTaskListMetadata>;

export type FindTaskListRejectedAction
    = Action<QualityHubActionTypes.FindTaskListRejected>
    & ActionWithMetadata<FindTaskListMetadata>
    & AxiosErrorAction;

export type FindTaskListFulfilledAction
    = Action<QualityHubActionTypes.FindTaskListFulfilled>
    & ActionWithMetadata<FindTaskListMetadata>
    & {
        /**
         * The resolved promise of a task list.
         */
        payload: Spintr.TaskList;
    };

export type CreateTaskStatusMetadata = {
    /**
     * The model that was sent to the server to create the task status.
     */
    model: TaskStatusModel;

    /**
     * The ID of the task list to which the item belongs.
     */
    listId: number;
};

export type CreateTaskStatusAction
    = Action<QualityHubActionTypes.CreateTaskStatus>
    & ActionWithMetadata<CreateTaskStatusMetadata>
    & {
        /**
         * The promise to be handled by the thunk middleware.
         */
        payload: Promise<Spintr.TaskListItemStatus>;
    };

export type CreateTaskStatusPendingAction
    = Action<QualityHubActionTypes.CreateTaskStatusPending>
    & ActionWithMetadata<CreateTaskStatusMetadata>;

export type CreateTaskStatusRejectedAction
    = Action<QualityHubActionTypes.CreateTaskStatusRejected>
    & ActionWithMetadata<CreateTaskStatusMetadata>
    & AxiosErrorAction;

export type CreateTaskStatusFulfilledAction
    = Action<QualityHubActionTypes.CreateTaskStatusFulfilled>
    & ActionWithMetadata<CreateTaskStatusMetadata>
    & {
        /**
         * The resolved promise of the created task status.
         */
        payload: Spintr.TaskListItemStatus;
    };

/**
 * The metadata associated with the UpdateTaskStatus actions.
 */
export type UpdateTaskStatusMetadata = {
    /**
     * The status, as it was, before the update. Used for optimistic updates.
     */
    status: Spintr.TaskListItemStatus;

    /**
     * The ID of the task list to which the status belongs.
     */
    listId: number;

    /**
     * The model that was sent to the server to update the task status.
     */
    model: TaskStatusModel;
};

/**
 * An action that indicates a task list item status update is being requested.
 */
export type UpdateTaskStatusAction
    = Action<QualityHubActionTypes.UpdateTaskStatus>
    & ActionWithMetadata<UpdateTaskStatusMetadata>
    & {
        /**
         * The promise to be handled by the thunk middleware.
         */
        payload: Promise<Spintr.TaskListItemStatus>;
    };

/**
 * An action that indicates a task list item status update is pending.
 */
export type UpdateTaskStatusPendingAction
    = Action<QualityHubActionTypes.UpdateTaskStatusPending>
    & ActionWithMetadata<UpdateTaskStatusMetadata>;

/**
 * An action that indicates a task list item status update has failed.
 */
export type UpdateTaskStatusRejectedAction
    = Action<QualityHubActionTypes.UpdateTaskStatusRejected>
    & ActionWithMetadata<UpdateTaskStatusMetadata>
    & AxiosErrorAction;

/**
 * An action that indicates a task list item status has been updated.
 */
export type UpdateTaskStatusFulfilledAction
    = Action<QualityHubActionTypes.UpdateTaskStatusFulfilled>
    & ActionWithMetadata<UpdateTaskStatusMetadata>
    & {
        /**
         * The resolved promise of the updated task status.
         */
        payload: Spintr.TaskListItemStatus;
    };

/**
 * The metadata associated with the DeleteTaskStatus actions.
 */
export type DeleteTaskStatusMetadata = {
    /**
     * The status that was deleted.
     */
    status: Spintr.TaskListItemStatus;

    /**
     * The ID of the task list to which the item belongs.
     */
    listId: number;
};

export type DeleteTaskStatusAction
    = Action<QualityHubActionTypes.DeleteTaskStatus>
    & ActionWithMetadata<DeleteTaskStatusMetadata>
    & {
        /**
         * The promise to be handled by the thunk middleware.
         */
        payload: Promise<void>;
    };

export type DeleteTaskStatusPendingAction
    = Action<QualityHubActionTypes.DeleteTaskStatusPending>
    & ActionWithMetadata<DeleteTaskStatusMetadata>;

export type DeleteTaskStatusRejectedAction
    = Action<QualityHubActionTypes.DeleteTaskStatusRejected>
    & ActionWithMetadata<DeleteTaskStatusMetadata>
    & AxiosErrorAction;

export type DeleteTaskStatusFulfilledAction
    = Action<QualityHubActionTypes.DeleteTaskStatusFulfilled>
    & ActionWithMetadata<DeleteTaskStatusMetadata>;

/**
 * An action that indicates a resource tags query has been requested.
 */
export type QueryResourceTagsAction
    = Action<QualityHubActionTypes.QueryResourceTags>
    & { payload: Promise<Spintr.QueryEnvelope<Spintr.ResourceTag>>; };

/**
 * An action that indicates resource tags are being queried.
 */
export type QueryResourceTagsPendingAction
    = Action<QualityHubActionTypes.QueryResourceTagsPending>;

/**
 * An action that indicates resource tags query has failed.
 */
export type QueryResourceTagsRejectedAction
    = Action<QualityHubActionTypes.QueryResourceTagsRejected>
    & AxiosErrorAction;

/**
 * An action that indicates resource tags query has succeeded.
 */
export type QueryResourceTagsFulfilledAction
    = Action<QualityHubActionTypes.QueryResourceTagsFulfilled>
    & {
        /**
         * The resolved promise of the resource tags.
         */
        payload: Spintr.QueryEnvelope<Spintr.ResourceTag>;
    };

/**
 * A base type for offline operations related to the Quality Hub.
 */
type OfflineOperationBase = {
    /**
     * A unique identifier for the operation.
     */
    id: string;
};

/**
 * An offline operation for the creation of a task status.
 */
type CreateStatusOperation = OfflineOperationBase & {
    data: CreateTaskStatusMetadata;

    type: "CREATE_STATUS";
};

/**
 * An offline operation for the modification of a task status.
 */
type UpdateStatusOperation = OfflineOperationBase & {
    data: UpdateTaskStatusMetadata;

    type: "UPDATE_STATUS";
};

/**
 * An offline operation for the deletion of a task status.
 */
type DeleteStatusOperation = OfflineOperationBase & {
    data: DeleteTaskStatusMetadata;

    type: "DELETE_STATUS";
};

/**
 * A union type that represents all possible offline operations related to the 
 * Quality Hub.
 */
export type OfflineQueueOperation
    = CreateStatusOperation
    | UpdateStatusOperation
    | DeleteStatusOperation;

/**
 * An action that indicates an operation should be added to the offline queue.
 */
export type AddToOfflineQueueAction
    = Action<QualityHubActionTypes.AddToOfflineQueue>
    & { operation: OfflineQueueOperation; };

/**
 * An action that indicates a task should be removed from the offline queue.
 */
export type RemoveFromOfflineQueueAction
    = Action<QualityHubActionTypes.RemoveFromOfflineQueue>
    & { operationId: string; };

export type QueryOperationalTargetsAction
    = Action<QualityHubActionTypes.QueryOperationalTargets>
    & {
        /**
         * The promise to be handled by the thunk middleware.
         */
        payload: Promise<Spintr.QueryEnvelope<Spintr.OperationalTarget>>;
    };

export type QueryOperationalTargetsPendingAction
    = Action<QualityHubActionTypes.QueryOperationalTargetsPending>;

export type QueryOperationalTargetsRejectedAction
    = Action<QualityHubActionTypes.QueryOperationalTargetsRejected>;

export type QueryOperationalTargetsFulfilledAction
    = Action<QualityHubActionTypes.QueryOperationalTargetsFulfilled>
    & {
        /**
         * The resolved promise of the operational targets.
         */
        payload: Spintr.QueryEnvelope<Spintr.OperationalTarget>;
    };

/**
 * The metadata associated with the CreateOperationalTarget actions.
 */
export type CreateOperationalTargetMetadata = {
    /**
     * The model that was sent to the server to create the operational target.
     */
    model: OperationalTargetModel;
};

/**
 * An action that indicates a operational target creation is being requested.
 */
export type CreateOperationalTargetAction
    = Action<QualityHubActionTypes.CreateOperationalTarget>
    & ActionWithMetadata<CreateOperationalTargetMetadata>
    & {
        /**
         * The promise to be handled by the thunk middleware.
         */
        payload: Promise<Spintr.OperationalTarget>;
    };

/**
 * An action that indicates a operational target creation is pending.
 */
export type CreateOperationalTargetPendingAction
    = Action<QualityHubActionTypes.CreateOperationalTargetPending>
    & ActionWithMetadata<CreateOperationalTargetMetadata>

/**
 * An action that indicates a operational target creation has failed.
 */
export type CreateOperationalTargetRejectedAction
    = Action<QualityHubActionTypes.CreateOperationalTargetRejected>
    & ActionWithMetadata<CreateOperationalTargetMetadata>
    & AxiosErrorAction;

/**
 * An action that indicates a operational target has been created.
 */
export type CreateOperationalTargetFulfilledAction
    = Action<QualityHubActionTypes.CreateOperationalTargetFulfilled>
    & ActionWithMetadata<CreateOperationalTargetMetadata>
    & {
        /**
         * The resolved promise of the created operational target.
         */
        payload: Spintr.OperationalTarget;
    };

/**
 * The metadata associated with the UpdateOperationalTarget actions.
 */
export type UpdateOperationalTargetMetadata = {
    /**
     * The ID of the operational target being updated.
     */
    id: number;

    /**
     * The model that was sent to the server to update the operational target.
     */
    model: OperationalTargetModel;
};

/**
 * An action that indicates a operational target update is being requested.
 */
export type UpdateOperationalTargetAction
    = Action<QualityHubActionTypes.UpdateOperationalTarget>
    & ActionWithMetadata<UpdateOperationalTargetMetadata>
    & {
        /**
         * The promise to be handled by the thunk middleware.
         */
        payload: Promise<Spintr.OperationalTarget>;
    };

/**
 * An action that indicates a operational target update is pending.
 */
export type UpdateOperationalTargetPendingAction
    = Action<QualityHubActionTypes.UpdateOperationalTargetPending>
    & ActionWithMetadata<UpdateOperationalTargetMetadata>;

/**
 * An action that indicates a operational target update has failed.
 */
export type UpdateOperationalTargetRejectedAction
    = Action<QualityHubActionTypes.UpdateOperationalTargetRejected>
    & ActionWithMetadata<UpdateOperationalTargetMetadata>
    & AxiosErrorAction;

/**
 * An action that indicates a operational target has been updated.
 */
export type UpdateOperationalTargetFulfilledAction
    = Action<QualityHubActionTypes.UpdateOperationalTargetFulfilled>
    & ActionWithMetadata<UpdateOperationalTargetMetadata>
    & {
        /**
         * The resolved promise of the updated operational target.
         */
        payload: Spintr.OperationalTarget;
    };

export type DeleteOperationalTargetMetadata = {
    /**
     * The operational target that was deleted.
     */
    target: Spintr.OperationalTarget;
};

export type DeleteOperationalTargetAction
    = Action<QualityHubActionTypes.DeleteOperationalTarget>
    & ActionWithMetadata<DeleteOperationalTargetMetadata>
    & {
        /**
         * The promise to be handled by the thunk middleware.
         */
        payload: Promise<void>;
    };

export type DeleteOperationalTargetPendingAction
    = Action<QualityHubActionTypes.DeleteOperationalTargetPending>
    & ActionWithMetadata<DeleteOperationalTargetMetadata>;

export type DeleteOperationalTargetRejectedAction
    = Action<QualityHubActionTypes.DeleteOperationalTargetRejected>
    & ActionWithMetadata<DeleteOperationalTargetMetadata>
    & AxiosErrorAction;

export type DeleteOperationalTargetFulfilledAction
    = Action<QualityHubActionTypes.DeleteOperationalTargetFulfilled>
    & ActionWithMetadata<DeleteOperationalTargetMetadata>;

export type OperationalTargetCreatedAction
    = Action<QualityHubActionTypes.OperationalTargetCreated>
    & { target: Spintr.OperationalTarget; };

export type OperationalTargetUpdatedAction
    = Action<QualityHubActionTypes.OperationalTargetUpdated>
    & { target: Spintr.OperationalTarget; };

export type OperationalTargetDeletedAction
    = Action<QualityHubActionTypes.OperationalTargetDeleted>
    & { targetId: number; };

/**
 * A union type that represents all possible actions related to the Quality Hub.
 */
export type QualityHubAction
    = TaskListCreatedAction
    | TaskListUpdatedAction
    | TaskListDeletedAction
    | TaskListGroupCreatedAction
    | TaskListGroupUpdatedAction
    | TaskListGroupDeletedAction
    | TaskListItemCreatedAction
    | TaskListItemUpdatedAction
    | TaskListItemDeletedAction
    | SetTaskListGroupOverridesAction
    | SetTaskListGroupOverridesPendingAction
    | SetTaskListGroupOverridesRejectedAction
    | SetTaskListGroupOverridesFulfilledAction
    | UpdateTaskListItemOverridesAction
    | UpdateTaskListItemOverridesPendingAction
    | UpdateTaskListItemOverridesRejectedAction
    | UpdateTaskListItemOverridesFulfilledAction
    | TaskListItemStatusCreatedAction
    | TaskListItemStatusUpdatedAction
    | TaskListItemStatusDeletedAction
    | ResourceTagCreatedAction
    | ResourceTagUpdatedAction
    | ResourceTagDeletedAction
    | QueryMyTaskListsAction
    | QueryMyTaskListsPendingAction
    | QueryMyTaskListsRejectedAction
    | QueryMyTaskListsFulfilledAction
    | FindTaskListAction
    | FindTaskListPendingAction
    | FindTaskListRejectedAction
    | FindTaskListFulfilledAction
    | CreateTaskStatusAction
    | CreateTaskStatusPendingAction
    | CreateTaskStatusRejectedAction
    | CreateTaskStatusFulfilledAction
    | UpdateTaskStatusAction
    | UpdateTaskStatusPendingAction
    | UpdateTaskStatusRejectedAction
    | UpdateTaskStatusFulfilledAction
    | DeleteTaskStatusAction
    | DeleteTaskStatusPendingAction
    | DeleteTaskStatusRejectedAction
    | DeleteTaskStatusFulfilledAction
    | QueryResourceTagsAction
    | QueryResourceTagsPendingAction
    | QueryResourceTagsRejectedAction
    | QueryResourceTagsFulfilledAction
    | RemoveFromOfflineQueueAction
    | AddToOfflineQueueAction
    | QueryOperationalTargetsAction
    | QueryOperationalTargetsPendingAction
    | QueryOperationalTargetsRejectedAction
    | QueryOperationalTargetsFulfilledAction
    | CreateOperationalTargetAction
    | CreateOperationalTargetPendingAction
    | CreateOperationalTargetRejectedAction
    | CreateOperationalTargetFulfilledAction
    | UpdateOperationalTargetAction
    | UpdateOperationalTargetPendingAction
    | UpdateOperationalTargetRejectedAction
    | UpdateOperationalTargetFulfilledAction
    | DeleteOperationalTargetAction
    | DeleteOperationalTargetPendingAction
    | DeleteOperationalTargetRejectedAction
    | DeleteOperationalTargetFulfilledAction
    | OperationalTargetCreatedAction
    | OperationalTargetUpdatedAction
    | OperationalTargetDeletedAction
    ;

